// src/components/dashboard/FilterPanel.jsx
import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from "react";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import DateRangePicker from "../../../DatePicker";
import { ActiveFilterChip } from "./ActiveFilterChip";

const defaultPeriods = [
  { value: "yesterday", label: "Yesterday" },
  { value: "weekly", label: "Last 7 Days" },
  { value: "monthly", label: "Last 30 Days" },
  { value: "yearly", label: "Last Year" },
  { value: "custom", label: "Custom" },
];

const FilterPanel = forwardRef((props, ref) => {
  const {
    isOpen,
    onClose,
    activeFilters = [],
    selectedPeriod,
    onPeriodChange,
    tempDateRange,
    onTempDateChange,
    onApply,
    onReset,
    showCustomPicker = true,
    extraContent,
    position = "bottom-right",
    dropdownOpen,
    setDropdownOpen,
  } = props;

  const panelRef = useRef(null);
  const buttonRef = useRef(null);
  const [dropdownWidth, setDropdownWidth] = useState(null);

  useImperativeHandle(ref, () => panelRef.current);

  useEffect(() => {
    if (dropdownOpen && buttonRef.current) {
      setDropdownWidth(buttonRef.current.offsetWidth);
    }
  }, [dropdownOpen]);

  if (!isOpen) return null;

  const positionClass = {
    "bottom-right": "top-full mt-2 right-0",
    "bottom-left": "top-full mt-2 left-0",
    "top-right": "bottom-full mb-2 right-0",
    "top-left": "bottom-full mb-2 left-0",
  }[position];

  const currentLabel = defaultPeriods.find(p => p.value === selectedPeriod)?.label || "Custom";

  const handlePeriodSelect = (value) => {
    onPeriodChange(value);
    setDropdownOpen(false);

    if (value !== "custom") {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      const start = new Date(today);

      switch (value) {
        case "yesterday":
          start.setDate(today.getDate() - 1);
          today.setDate(today.getDate() - 1);
          break;
        case "weekly":
          start.setDate(today.getDate() - 7);
          break;
        case "monthly":
          start.setDate(today.getDate() - 30);
          break;
        case "yearly":
          start.setFullYear(today.getFullYear() - 1);
          break;
      }

      start.setHours(0, 0, 0, 0);
      onTempDateChange([start, today]);
    } else {
      onTempDateChange([null, null]);
    }
  };

  return (
    <div
      ref={panelRef}
      className={`absolute ${positionClass} bg-[#0a1733] border border-[#5A4479] rounded-lg shadow-lg p-4 z-50 min-w-[320px]`}
    >
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4 px-4 pt-4">
        <h4 className="text-[#D3B4FF] font-medium">Filter</h4>
        <button onClick={onClose} className="text-[#CCCCCC] hover:text-[#D3B4FF]">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* ACTIVE FILTERS */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 px-4 mb-4">
          {activeFilters.map((filter, i) => (
            <ActiveFilterChip key={i} label={filter.label} onRemove={filter.onRemove} />
          ))}
        </div>
      )}

      

      {/* DATE RANGE DROPDOWN */}
      <div className="px-4 mb-4 relative">
        {extraContent}
        <label className="block text-[#CCCCCC] text-sm font-medium mb-2">Date Range</label>
        <button
          ref={buttonRef}
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="w-full flex justify-between items-center px-3 py-2 bg-[#0a1733] border border-[#5A4479] rounded-md text-[#D3B4FF] text-sm"
        >
          <span className="truncate">{currentLabel}</span>
          {dropdownOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {dropdownOpen && (
          <div
            style={{ width: dropdownWidth, left: '16px' }}
            className="absolute z-10 mt-1 left-0 bg-[#0a1733] border border-[#5A4479] rounded-md shadow-lg"
          >
            {defaultPeriods.map((opt) => (
              <div
                key={opt.value}
                onClick={() => handlePeriodSelect(opt.value)}
                className="px-3 py-2 text-sm text-[#D3B4FF] hover:bg-[#2A2A2A] cursor-pointer"
              >
                {opt.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CUSTOM DATE PICKER */}
      {showCustomPicker && selectedPeriod === "custom" && (
        <div className="px-4 mb-4">
          <label className="block text-[#CCCCCC] text-sm font-medium mb-2">
            Select Custom Range
          </label>
          <DateRangePicker popperPlacement="top-start" dateRange={tempDateRange} setDateRange={onTempDateChange} />
        </div>
      )}

      {/* BUTTONS */}
      <div className="flex gap-3 px-4 pb-4">
        <button
          onClick={onReset}
          className="flex-1 px-4 py-2 border border-gray-600 rounded-full text-gray-300 hover:bg-gray-700/50 text-sm"
        >
          Reset
        </button>
        <button
          onClick={onApply}
          className="flex-1 px-4 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full text-sm hover:opacity-90"
        >
          Apply
        </button>
      </div>
    </div>
  );
});

FilterPanel.displayName = "FilterPanel";

export default FilterPanel;
